import { useEffect, useState } from "react";
import { links } from "../Data.jsx";
import { FaTwitter, FaLinkedin } from "react-icons/fa";
import { BsSun, BsMoon } from "react-icons/bs";
import { HiMenu } from "react-icons/hi";
import { IoClose, IoHome } from "react-icons/io5";
import { MdDesignServices, MdWorkOutline } from "react-icons/md";
import { AiOutlineProject } from "react-icons/ai";
import { BiEnvelope } from "react-icons/bi";
import { Link } from "react-scroll";
import { animateScroll } from "react-scroll";

const getStorageTheme = () => {
  let theme = 'light-theme';
  if (localStorage.getItem('theme')) {
    theme = localStorage.getItem('theme');
  }
  return theme;
}

// Icon mapping helper
const getNavIcon = (iconName) => {
  const icons = {
    home: <IoHome />,
    services: <MdDesignServices />,
    projects: <AiOutlineProject />,
    experience: <MdWorkOutline />,
    connect: <BiEnvelope />
  };
  return icons[iconName] || null;
};

const Header = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [scrollNav, setScrollNav] = useState(false);
  const [theme, setTheme] = useState(getStorageTheme());
  const [activeSection, setActiveSection] = useState('home');

  const scrollTop = () => {
    animateScroll.scrollToTop()
  };

  const changeNav = () => {
    if (window.scrollY >= 80) {
      setScrollNav(true)
    }
    else {
      setScrollNav(false)
    }
  }

  const toggleTheme = () => {
    if (theme === 'light-theme') {
      setTheme('dark-theme')
    }
    else {
      setTheme('light-theme')
    }
  }

  // Intersection Observer for viewport-based section detection
  useEffect(() => {
    const sections = links.map(link => document.getElementById(link.path)).filter(Boolean);

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -20% 0px', // Focus on middle 60% of viewport
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
    };

    const observerCallback = (entries) => {
      // Find the section with the highest intersection ratio
      let maxRatio = 0;
      let mostVisibleSection = null;

      entries.forEach(entry => {
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisibleSection = entry.target.id;
        }
      });

      if (mostVisibleSection && maxRatio > 0.1) {
        setActiveSection(mostVisibleSection);
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', changeNav);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", showMenu);
  }, [showMenu]);

  useEffect(() => {
    document.documentElement.className = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);

  // bg-bg-alt only applies when scroll-header is active, otherwise transparent/default
  const headerClass = scrollNav
    ? "fixed top-0 w-full px-[20px] py-[15px] z-[100] bg-bg-alt animate-[header_animate_0.8s_var(--transition)_0s_forwards]"
    : "absolute top-0 w-full px-[20px] lg:px-[30px] py-[25px] z-[100]";

  return (
    <header className={headerClass}>
      <nav className="flex justify-between items-center relative">
        <Link to='/' onClick={scrollTop} className="font-bold cursor-pointer text-large flex items-center leading-relaxed gap-2">
          <img src="/logo1.png" alt="" className="w-8 h-auto" />
          <span>Harshal</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
          {links.map(({ name, path }, index) => {
            const isActive = activeSection === path;
            return (
              <Link
                key={index}
                className={`text-large font-bold cursor-pointer transition-all duration-300 ease-custom hover:text-primary nav-link-desktop ${isActive ? '!text-primary' : 'text-title'
                  }`}
                to={path}
                spy={false}
                hashSpy={false}
                smooth={true}
                offset={-150}
                duration={500}
              >
                {name}
              </Link>
            );
          })}
        </div>



        {/* Mobile Dropdown Menu */}
        <div className={`${showMenu ? "max-h-screen opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-4"} lg:hidden absolute top-full right-[-10px] w-52 bg-bg-alt shadow-2xl rounded-2xl overflow-visible transition-all duration-300 ease-custom border border-border mt-3`}>
          {/* Arrow pointing to hamburger icon */}
          <div className="absolute -top-2 right-4 w-4 h-4 bg-bg-alt border-t border-l border-border rotate-45 transform"></div>
          <div className="p-5 relative">
            <ul className="flex flex-col gap-3">
              {links.map(({ name, path, icon }, index) => {
                return (
                  <li key={index}>
                    <Link
                      className={`text-h6 font-bold cursor-pointer transition-all duration-300 ease-custom hover:text-primary flex items-center gap-3 ${activeSection === path ? '!text-primary' : 'text-title'
                        }`}
                      to={path}
                      spy={false}
                      hashSpy={false}
                      smooth={true}
                      offset={-150}
                      duration={500}
                      onClick={() => setShowMenu(false)}
                    >
                      <span className="text-xl">{getNavIcon(icon)}</span>
                      {name}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="flex gap-5 mt-4 pt-4 border-t border-border/30 justify-center">
              <a href="https://twitter.com/Harshal__Rajput" target="_blank" rel="noreferrer" className="text-title text-xl transition-all duration-300 ease-custom hover:text-primary hover:scale-110">
                <FaTwitter />
              </a>
              <a href="https://www.linkedin.com/in/harshal-rajput-94755a228/" target="_blank" rel="noreferrer" className="text-title text-xl transition-all duration-300 ease-custom hover:text-primary hover:scale-110">
                <FaLinkedin />
              </a>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <div className="text-h4 flex items-center cursor-pointer" onClick={toggleTheme}>
            {theme === 'light-theme' ? <BsMoon /> : <BsSun />}
          </div>

          <div
            className="text-h3 flex items-center cursor-pointer z-[100] lg:hidden text-title"
            onClick={() => setShowMenu(!showMenu)}
          >
            {showMenu ? <IoClose /> : <HiMenu />}
          </div>
        </div>
      </nav>
      {/* Keyframe animation definition specifically for header */}
      <style>{`
        @keyframes header_animate {
          0% { transform: translateY(-100px); }
          100% { transform: translateY(0); }
        }
      `}</style>
    </header >
  );
};

export default Header;

